/**
 * SEO field resolution (STO-2). Pimcore returns strict, per-language raw SEO values (empty → null,
 * no inheritance/cross-language fallback). All fallbacks live here on the frontend and are shared
 * by product pages (fallback = Vendure data) and content pages (fallback = CMS content).
 */

/** The strict SEO block as returned by Pimcore (`productSeo` / ContentPage `seo`). */
export interface PimcoreSeoFields {
  seoTitle?: string | null
  seoDescription?: string | null
  seoKeywords?: string | null
  seoNoIndex?: boolean | null
  seoCanonical?: string | null
  seoOgImage?: { url?: string | null } | null
}

/** Fallback values (source-specific) used when the Pimcore field is empty. */
export interface SeoFallback {
  title?: string | null
  description?: string | null
  ogImage?: string | null
}

/** Props shape consumed by the `<Seo>` component. */
export interface ResolvedSeo {
  title: string | null
  /** Title comes from Pimcore `seoTitle` (editor-authored, already complete) → the `<Seo>`
   *  site-name suffix must NOT be appended. Only fallback titles get the suffix. */
  titleIsComplete: boolean
  description?: string
  keywords?: string
  ogImage?: string
  noIndex: boolean
  canonicals?: Array<{ absolutePath: string }>
}

const decodeEntities = (s: string): string =>
  s
    .replace(/&(?:auml|#228);/g, 'ä').replace(/&(?:ouml|#246);/g, 'ö').replace(/&(?:uuml|#252);/g, 'ü')
    .replace(/&(?:Auml|#196);/g, 'Ä').replace(/&(?:Ouml|#214);/g, 'Ö').replace(/&(?:Uuml|#220);/g, 'Ü')
    .replace(/&(?:szlig|#223);/g, 'ß')
    .replace(/&nbsp;/g, ' ').replace(/&quot;/g, '"').replace(/&#0?39;|&apos;/g, "'")
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&')

/**
 * Turn Vendure rich-text HTML into a plain-text meta description (~160 chars). Strips tags and
 * decodes the entities common in German rich text so umlauts don't leak as "&uuml;".
 */
export const htmlToMetaDescription = (html?: string | null): string | undefined => {
  if (!html) return undefined
  const text = decodeEntities(html.replace(/<[^>]*>/g, ' ')).replace(/\s+/g, ' ').trim()
  if (!text) return undefined
  return text.length > 160 ? `${text.slice(0, 157).trimEnd()}…` : text
}

/**
 * Cascade several Pimcore SEO layers into one, highest priority first (e.g. variant → master).
 * Each field independently takes the first layer that has a non-empty value — so a variant that
 * only sets `seoTitle` still inherits `seoDescription` from the master. The result feeds `resolveSeo`.
 */
export function mergePimcoreSeo(...layers: Array<PimcoreSeoFields | null | undefined>): PimcoreSeoFields {
  const firstText = (get: (l: PimcoreSeoFields) => string | null | undefined): string | null => {
    for (const l of layers) { const v = l && get(l); if (v) return v }
    return null
  }
  const ogImage = (): PimcoreSeoFields['seoOgImage'] => {
    for (const l of layers) { if (l?.seoOgImage?.url) return l.seoOgImage }
    return null
  }
  const noIndex = (): boolean | null => {
    for (const l of layers) { if (l && l.seoNoIndex != null) return l.seoNoIndex }
    return null
  }
  return {
    seoTitle: firstText(l => l.seoTitle),
    seoDescription: firstText(l => l.seoDescription),
    seoKeywords: firstText(l => l.seoKeywords),
    seoNoIndex: noIndex(),
    seoCanonical: firstText(l => l.seoCanonical),
    seoOgImage: ogImage(),
  }
}

/**
 * Merge Pimcore SEO with per-page fallbacks. `||` (not `??`) so empty strings also fall back.
 * `seoNoIndex` defaults to `false` (indexable); `seoCanonical` becomes the single canonical override.
 */
export function resolveSeo(seo: PimcoreSeoFields | null | undefined, fallback: SeoFallback = {}): ResolvedSeo {
  return {
    title: seo?.seoTitle || fallback.title || null,
    titleIsComplete: !!seo?.seoTitle,
    description: seo?.seoDescription || fallback.description || undefined,
    keywords: seo?.seoKeywords || undefined,
    ogImage: seo?.seoOgImage?.url || fallback.ogImage || undefined,
    noIndex: seo?.seoNoIndex ?? false,
    canonicals: seo?.seoCanonical ? [{ absolutePath: seo.seoCanonical }] : undefined,
  }
}
